/**
 * Offline side activities (order 535, with a small rival-driver system at 266): things to do between
 * story jobs that pay, cost or unlock something, all saved (`SaveData.activities`, save v5).
 *
 *  - **Street races** (race.ts): three night courses from the Basin Crane Quay, stakes and a pot, rivals on
 *    the road graph, best times. Unlocked with the street jobs (Borrowed Time).
 *  - **Minicab shifts** (cab.ts): "the knowledge" for Kestrel Cars from the Bell Street phone box; fares
 *    pay the meter and tips for speed and care. Same unlock.
 *  - **Halloran's workshop** (garage.ts, garageScreen.ts): respray, metallic, wheels, stripes / roof /
 *    band, plates, a small offline engine tune and body work — a money sink. The build sheet of "your
 *    motor" is saved and the car comes back at Halloran's (or in the lockup) every session.
 *  - **Matchbooks** (matchbooks.ts): sixteen hidden collectibles; Lenny pays per number and at milestones.
 *
 * Races and shifts are `MissionDef`s handed to the mission system (`registerActivities`), so they share
 * its givers, intro cards, objective clipping, result sheets, NICKED / WASTED handling and the job board
 * that the little black book and the "what next" tracker read. Everything here is offline only: online
 * the givers are shut by the mission system, and the workshop, matchbooks and motor stay dormant.
 */
import * as THREE from 'three';
import type { GameContext } from '../core/context';
import { distance, forward, type V2 } from '../core/math';
import type { System } from '../core/system';
import { MOTOR_TAG, type Vehicle } from '../vehicles/types';
import { getSpec } from '../vehicles/specs';
import { CAR_LOOK_KEY, CAR_LOOK_VERSION_KEY, carTraits, type CarLook } from '../vehicles/carTraits';
import type { BookEntry } from '../book/bookScreen';
import { formatMoney, LOCKUP_UNLOCK_ID, repairCost } from '../economy/wallet';
import { contactPerk } from '../contacts/perks';
import { playerLockupPoi } from '../economy/playerLockup';
import { UNLOCK_STREET_JOBS } from '../missions/ids';
import { planRoute } from '../missions/autopilot';
import type { MissionDef } from '../missions/types';
import { touchUi } from '../input/touch';
import { PAVE_Y } from '../world/view/ground';
import type { CityLayout, PointOfInterest } from '../world/types';
import { ActivityTicket } from './activityHud';
import { CAB_ID, CAB_SHIFTS, announceShift, cabDef, type FareGeometry } from './cab';
import {
  FAVOUR_DISCOUNT,
  GARAGE_OWNER,
  GARAGE_POI,
  PAINTS,
  PLATES,
  PRICES,
  TRIM_COLOURS,
  TUNES,
  UNLOCK_DES_FAVOUR,
  WHEELS,
  canWorkOn,
  paintName,
  price,
  trimName,
  tuneFor,
} from './garage';
import { GarageScreen, type GarageModel, type GarageRow } from './garageScreen';
import type { ActivityHost, TicketInfo } from './host';
import { MATCHBOOK_GLINT, MATCHBOOK_REACH, MATCHBOOK_SPOTS, matchbookPos, type MatchbookSpot } from './matchbooks';
import { RACE_ID, RaceSession, announceCourse, raceDef, type RaceRuntime } from './race';
import { RACE_TIERS_DEF } from './races';
import { kerbStop, raceLine, routeLength, type RaceLine } from './roadLine';
import { MATCHBOOK_FIND, dueMilestones, formatRaceTime } from './scoring';
import { CAB_TIERS, RACE_TIERS, emptyBuild, type ActivitiesSave, type CarBuild } from './state';

export interface GarageService {
  /**
   * Put the motor's build (paint, look, tune) on a freshly spawned car of the same model — the economy's
   * lockup car, which is your motor's model once you have one (else the stock Vesper getaway car).
   */
  dress(v: Vehicle): void;
  /** Your motor's model (the save's one car), or null before you have one. */
  motorSpec(): string | null;
  /** Is this car yours: your motor, or the lockup's own getaway car? */
  isMine(v: Vehicle): boolean;
  /** The build sheet for this car: the motor's own if it is the motor, else what it looks like now. */
  buildOf(v: Vehicle): CarBuild;
  /** Put a build on a car (paint, look, tune) without paying or saving it: Pike's spray bay's live preview. */
  preview(v: Vehicle, b: CarBuild): void;
  /** Paid for (Pike's spray bay, src/garage): this car becomes your motor with this build, saved. */
  keep(v: Vehicle, b: CarBuild): void;
}

export interface ActivityService {
  /** The race running now (rivals, standings), or null. */
  readonly race: RaceSession | null;
  /** Matchbooks found / total. */
  readonly matchbooks: { found: number; total: number };
}

declare module '../core/services' {
  interface ServiceMap {
    activities: ActivityService;
    garage: GarageService;
  }
}

declare module '../core/events' {
  interface GameEvents {
    /** A matchbook was picked up (src/activities). */
    matchbookFound: { id: string; count: number; total: number };
    /** Des did a job on the player's motor (src/activities). */
    garageWork: { vehicleId: number; job: string; cost: number };
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Side activities read-out: race / cab records, matchbooks, the motor, the race in progress, the ticket. */
    activityState(): Record<string, unknown>;
    /** Choose the tier the race / cab giver offers next (≤ the highest unlocked). Returns the tier set. */
    activityTier(kind: 'race' | 'cab', tier: number): number;
    /** Every matchbook spot with its position and whether it's found. */
    matchbookSpots(): { id: string; venue: string; x: number; z: number; found: boolean }[];
    /** Where the race grid / the first fare / Halloran's bay is (test convenience). */
    activityPoint(what: 'race-grid' | 'cab-pickup' | 'garage'): { x: number; z: number } | null;
    /** Halloran's job card read-out (rows, open). */
    garageState(): Record<string, unknown>;
    /** Do a job at Halloran's from the driver's seat: press → `steps` times on that row, then pay. */
    garageBuy(job: string, steps?: number): { ok: boolean; reason: string | null };
  }
}

/** Halloran's job card opens from the forecourt while driving. */
const GARAGE_RADIUS = 8;

/** Shared between the driver system (266) and the activity system (535). */
interface Shared {
  race: RaceSession | null;
}

export function createActivitySystemFactories(): [() => System, () => System] {
  const shared: Shared = { race: null };
  return [() => createRivalDriverSystem(shared), () => createActivitySystem(shared)];
}

/** Rivals at the wheel, in the decisions band (writes `vehicle.controls` before the vehicles step). */
function createRivalDriverSystem(shared: Shared): System {
  return {
    id: 'activity-drivers',
    order: 266,
    fixedUpdate(dt) {
      shared.race?.drive(dt);
    },
  };
}

function createActivitySystem(shared: Shared): System {
  let ctx: GameContext;
  let ticket: ActivityTicket | null = null;
  let ticketState: TicketInfo | null = null;
  const offs: (() => void)[] = [];
  const lines: (RaceLine | null)[] = [];
  const raceDefs: (MissionDef | null)[] = [];
  const cabDefs: (MissionDef | null)[] = [];
  const spots: { spot: MatchbookSpot; pos: V2; alley: boolean }[] = [];
  let mbMesh: THREE.InstancedMesh | null = null;
  let mbGlint: THREE.Mesh | null = null;
  let mbDirty = true;
  let mbShown = true;
  let garagePoi: PointOfInterest | null = null;
  let motorId = -1;
  let screen: GarageScreen | null = null;
  let frozeTime = false;
  let time = 0;
  /** Book message keys posted this session (read-out for tests). */
  const posted: string[] = [];

  const save = () => ctx.services.get('save');
  const rec = (): ActivitiesSave => save().data.activities;
  const ui = () => ctx.services.tryGet('ui');
  const economy = () => ctx.services.tryGet('economy');
  /** Offline and the single-player wallet in charge. */
  const offline = (): boolean => !ctx.services.tryGet('net')?.online && (economy()?.authority ?? 'local') === 'local';
  const streetUnlocked = (): boolean => save().hasUnlock(UNLOCK_STREET_JOBS);
  const favour = (): boolean => save().hasUnlock(UNLOCK_DES_FAVOUR);

  const host: ActivityHost = {
    get ctx() {
      return ctx;
    },
    record: rec,
    dirty: () => save().markDirty(),
    canAfford: (n) => economy()?.canAfford(n) ?? ctx.state.money >= n,
    spend: (n, reason) => economy()?.spend(n, reason) ?? false,
    post: (m) => {
      if (m.key && !posted.includes(m.key)) posted.push(m.key);
      // Headless (tests) there's no page for the bleeper to buzz on; the keys above are the record.
      if (typeof window !== 'undefined') ctx.services.tryGet('book')?.post(m);
    },
    ticket: (t) => {
      ticketState = t;
      ticket?.set(t);
    },
  };

  const raceRuntime: RaceRuntime = {
    session: () => shared.race,
    open(tier, line) {
      shared.race?.dispose();
      shared.race = new RaceSession(ctx, tier, line);
      return shared.race;
    },
    close() {
      shared.race?.dispose();
      shared.race = null;
    },
  };

  // ------------------------------------------------------------------ definitions

  function buildDefs(): void {
    const layout = ctx.services.get('layout');
    for (const t of RACE_TIERS_DEF) {
      const line = raceLine(layout, t.route);
      lines.push(line);
      raceDefs.push(line ? raceDef(host, raceRuntime, t, line) : null);
    }
    for (const s of CAB_SHIFTS) {
      const pois = s.fares.flatMap((f) => [layout.pois.find((p) => p.id === f.from), layout.pois.find((p) => p.id === f.to)]);
      if (pois.some((p) => !p) || layout.roads.edges.length < 20) {
        cabDefs.push(null);
        continue;
      }
      const geo: FareGeometry[] = s.fares.map((f) => {
        const from = layout.pois.find((p) => p.id === f.from)!;
        const to = layout.pois.find((p) => p.id === f.to)!;
        const pickup = kerbStop(layout, from.pos);
        const drop = kerbStop(layout, to.pos);
        return { pickup, drop, metres: Math.max(80, routeLength([pickup, ...planRoute(layout, pickup, drop)])) };
      });
      cabDefs.push(cabDef(host, s, geo));
    }
  }

  /** What the givers offer right now: the race course and the cab shift that are next. */
  function offered(): MissionDef[] {
    const out: MissionDef[] = [];
    const r = raceDefs[rec().race.next - 1] ?? raceDefs[0];
    if (r) out.push(r);
    const c = cabDefs[rec().cab.next - 1] ?? cabDefs[0];
    if (c) out.push(c);
    return out;
  }

  // ------------------------------------------------------------------ book & announcements

  /** Book notes for whatever is open (each has a key, so it's only ever posted once per save). */
  function announce(): void {
    if (!offline() || !streetUnlocked()) return;
    if (raceDefs[0]) announceCourse(host, 1);
    if (cabDefs[0]) announceShift(host, 1);
    // A tier added since the save was made (Kiln Common's course and shift) and opened by the save's old
    // record: the note it would have had (keyed, so never twice).
    const a = rec();
    if (a.race.unlocked > 1 && raceDefs[a.race.unlocked - 1]) announceCourse(host, a.race.unlocked);
    if (a.cab.unlocked > 1 && cabDefs[a.cab.unlocked - 1]) announceShift(host, a.cab.unlocked);
    if (garagePoi) {
      host.post({
        key: 'garage-open',
        from: GARAGE_OWNER,
        kind: 'offer',
        subject: 'The workshop',
        text: `Bring your motor round and I’ll make something of it: paint, wheels, stripes, plates, and a bit more go under the bonnet if you’ve got the money.\n\nDrive onto the forecourt at Halloran Motors and press E. Not while the police are after you, mind.`,
        action: { label: 'Mark Halloran’s on the map', mark: { x: garagePoi.pos.x, z: garagePoi.pos.z, label: 'Des — Halloran Motors' } },
      });
    }
  }

  function jobsEntries(): BookEntry[] {
    if (!offline()) return [];
    const out: BookEntry[] = [];
    const a = rec();
    if (garagePoi && streetUnlocked()) {
      const motor = a.motor;
      const b = motor?.build;
      const done: string[] = [];
      if (b) {
        if (b.paint !== null) done.push(paintName(b.paint) + (b.metallic ? ', metallic' : ''));
        if (b.wheels) done.push(WHEELS.find((w) => w.id === b.wheels)?.name ?? b.wheels);
        if (b.tune > 0) done.push(`stage ${b.tune} tune`);
        if (b.plate) done.push(`plates "${b.plate}"`);
      }
      const p = garagePoi;
      out.push({
        id: 'side:garage',
        title: 'Halloran’s workshop',
        sub: `${GARAGE_OWNER} · Halloran Motors`,
        mark: 'new',
        kicker: 'Your motor',
        heading: 'Halloran’s workshop',
        body: [
          'New paint (metallic if you like), wheels, stripes or a vinyl roof, personal plates, an engine tune and body work. Drive onto the forecourt and press E (not while the police are after you).',
          motor
            ? `Your motor: a ${modelName(motor.spec)} — ${done.length ? done.join(', ') : 'nothing done yet'}. It waits ${inLockup() ? 'in your lockup' : 'at Halloran’s'}.`
            : 'Des keeps the build sheet of the last car he worked on: that one comes back every time.',
          ...(favour() ? [`Lenny’s favour: ${Math.round(FAVOUR_DISCOUNT * 100)} % off everything.`] : []),
          ...(contactPerk(ctx, 'halloran-work') > 0 ? [`Des’s trade terms: ${Math.round(contactPerk(ctx, 'halloran-work') * 100)} % off everything${favour() ? ', on top of Lenny’s favour' : ''}.`] : []),
        ].map(touchText),
        meta: `Spent at Halloran’s so far: £${formatMoney(a.garageSpent)}.`,
        actions: [{ label: 'Mark Halloran’s on the map', run: () => mark({ x: p.pos.x, z: p.pos.z, label: 'Des — Halloran Motors' }) }],
      });
    }
    // Your motor: the one car the save keeps — where it is and how to get it (Halloran's and Pike's work on it).
    if (streetUnlocked()) {
      const motor = a.motor;
      const lockup = playerLockupPoi(ctx.services.get('layout').pois);
      const keyed = save().hasUnlock(LOCKUP_UNLOCK_ID);
      const pike = ctx.services.get('layout').pois.find((q) => q.kind === 'resprayShop');
      const b = motor?.build;
      const work: string[] = [];
      if (b) {
        if (b.matt) work.push('flat matt');
        if (b.neon !== null) work.push('neon underglow');
        if (b.ride !== 0) work.push(b.ride < 0 ? 'lowered' : 'raised');
        if (b.camber > 0) work.push('cambered wheels');
        if (b.track > 0) work.push('wide track');
      }
      const where = !motor
        ? keyed
          ? 'Your car is the getaway car in Lockup 7, Pilcher Ginnel: roll the shutter up and it comes out.'
          : 'You haven’t got a car of your own yet. Get the key to Lockup 7 on Pilcher Ginnel, or have Pike keep one for you at his spray bay.'
        : inLockup()
          ? `Your car: a ${modelName(motor.spec)}${work.length ? ` (${work.join(', ')})` : ''}. It is kept in Lockup 7, Pilcher Ginnel — roll the shutter up and it comes out, with all its work.`
          : `Your car: a ${modelName(motor.spec)}${work.length ? ` (${work.join(', ')})` : ''}. It waits on Halloran’s forecourt until you have the key to Lockup 7.`;
      out.push({
        id: 'side:motor',
        title: 'Your motor',
        sub: motor ? modelName(motor.spec) : 'none yet',
        kicker: 'Your car',
        heading: 'Your motor',
        body: [
          where,
          'Pike’s spray bay (R on the map): drive in slowly for paint, wheels, neon and stance. Work on a car that isn’t yours is lost when you leave it — unless you have Pike keep it (one car at a time: it replaces the one you had).',
        ].map(touchText),
        actions: [
          ...(lockup && (keyed || !motor) ? [{ label: 'Mark Lockup 7 on the map', run: () => mark({ x: lockup.pos.x, z: lockup.pos.z, label: 'Lockup 7 — your car' }) }] : []),
          ...(motor && !inLockup() && garagePoi ? [{ label: 'Mark Halloran’s on the map', run: () => mark({ x: garagePoi!.pos.x, z: garagePoi!.pos.z, label: 'Your car — Halloran Motors' }) }] : []),
          ...(pike ? [{ label: 'Mark Pike’s spray bay', run: () => mark({ x: pike.pos.x, z: pike.pos.z, label: 'Pike’s spray bay' }) }] : []),
        ],
      });
    }
    if (a.matchbooks.length > 0) {
      const n = a.matchbooks.length;
      const next = [4, 8, 12, 16].find((c) => c > n);
      out.push({
        id: 'side:matchbooks',
        title: `Matchbooks — ${n} of ${MATCHBOOK_SPOTS.length}`,
        sub: 'Lenny the Ear pays for numbers',
        mark: n >= MATCHBOOK_SPOTS.length ? 'done' : 'new',
        kicker: 'Collecting',
        heading: 'Matchbooks',
        body: [
          `Books of matches from the borough’s pubs, clubs and caffs, each with a telephone number scrawled inside. Lenny pays £${MATCHBOOK_FIND} a number, more at 4, 8, 12 and all ${MATCHBOOK_SPOTS.length}.`,
          'They turn up in alleys, ginnels and back-street pavements. Walk over one to pick it up.',
          next ? `Next bonus at ${next}.` : 'You’ve found the lot. Des Halloran owes Lenny a favour now — and so, it turns out, you.',
        ].map(touchText),
        meta: `Found: ${a.matchbooks.map((id) => MATCHBOOK_SPOTS.find((s) => s.id === id)?.venue ?? id).join(', ')}.`,
      });
    }
    return out;
  }

  function mark(w: { x: number; z: number; label: string }): void {
    ctx.services.tryGet('onboarding')?.setWaypoint(w);
    ui()?.toast(`Route set: ${w.label}. Follow the arrows on the road.`, 'info', 2.6);
    ctx.services.tryGet('book')?.close();
  }

  function touchText(t: string): string {
    return touchUi.enabled ? t.replace(/\bpress E\b/g, 'tap USE').replace(/\bPress E\b/g, 'Tap USE') : t;
  }

  function modelName(spec: string): string {
    try {
      return getSpec(spec).displayName;
    } catch {
      return spec;
    }
  }

  /**
   * You have the lockup key: your motor (any model) lives in Lockup 7 and the shutter brings it out; nothing
   * waits at the yard. Without the key it waits on Halloran's forecourt.
   */
  function inLockup(): boolean {
    return !!rec().motor && save().hasUnlock(LOCKUP_UNLOCK_ID);
  }

  // ------------------------------------------------------------------ matchbooks

  function buildSpots(): void {
    const layout = ctx.services.get('layout');
    for (const spot of MATCHBOOK_SPOTS) {
      const pos = matchbookPos(layout, spot);
      if (!pos) continue;
      const a = layout.roads.edges.find((e) => e.name && (e.alley || e.sidewalk <= 0) && near(layout, e, pos));
      spots.push({ spot, pos, alley: !!a });
    }
  }

  function near(layout: CityLayout, e: { from: number; to: number }, p: V2): boolean {
    const a = layout.roads.nodes[e.from]!.pos;
    const b = layout.roads.nodes[e.to]!.pos;
    const abx = b.x - a.x;
    const abz = b.z - a.z;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.z - a.z) * abz) / (abx * abx + abz * abz || 1)));
    return Math.hypot(p.x - (a.x + abx * t), p.z - (a.z + abz * t)) < 2.5;
  }

  function checkMatchbooks(): void {
    if (!offline() || ctx.state.mode !== 'playing') return;
    const player = ctx.services.get('player');
    if (player.mode !== 'foot') return;
    const a = rec();
    for (const s of spots) {
      if (a.matchbooks.includes(s.spot.id)) continue;
      if (distance(player.pos, s.pos) > MATCHBOOK_REACH) continue;
      a.matchbooks = [...a.matchbooks, s.spot.id];
      mbDirty = true;
      const count = a.matchbooks.length;
      const total = spots.length;
      economy()?.earn(MATCHBOOK_FIND, 'matchbook');
      ui()?.toast(`Matchbook: ${s.spot.venue} — a number inside. Lenny pays £${MATCHBOOK_FIND}. (${count} of ${total})`, 'cash', 3.4);
      ctx.services.tryGet('audio')?.ui('confirm');
      ctx.events.emit('matchbookFound', { id: s.spot.id, count, total });
      if (count === 1) {
        host.post({
          key: 'matchbooks-first',
          from: 'Lenny the Ear',
          kind: 'tip',
          subject: 'Numbers',
          text: `Found a matchbook with a number in it? I’ll have that. Every number in this borough is worth something to someone. £${MATCHBOOK_FIND} a book, and a drink on top every four you bring me.\n\nThere’s ${total} of them about: alleys, ginnels, back pavements.`,
        });
      }
      for (const m of dueMilestones(count, a.matchbookMilestones)) {
        a.matchbookMilestones = [...a.matchbookMilestones, m.count].sort((x, y) => x - y);
        economy()?.earn(m.bonus, 'matchbook-bonus');
        const all = m.count >= total;
        if (all) save().addUnlock(UNLOCK_DES_FAVOUR);
        host.post({
          key: `matchbooks-${m.count}`,
          from: 'Lenny the Ear',
          kind: 'offer',
          subject: all ? 'The full set' : `${m.count} numbers`,
          text: all
            ? `The lot. Every number in Low Borough, and I owe you £${m.bonus} for it. One more thing: Des Halloran owed me a favour, and now he owes you one. ${Math.round(FAVOUR_DISCOUNT * 100)} % off anything at the workshop.`
            : `${m.count} numbers. Some very interesting people in there. £${m.bonus} on top, as promised.`,
        });
        ui()?.toast(`Lenny’s bonus: £${m.bonus} for ${m.count} matchbooks.`, 'cash', 3.4);
      }
      save().markDirty();
    }
  }

  function buildMatchbookView(): void {
    if (!ctx.render || mbMesh) return;
    const geo = new THREE.BoxGeometry(0.2, 0.03, 0.13);
    const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.7, metalness: 0, emissive: 0x2a2116, emissiveIntensity: 0.6 });
    mbMesh = new THREE.InstancedMesh(geo, mat, MATCHBOOK_SPOTS.length);
    mbMesh.name = 'matchbooks';
    mbMesh.frustumCulled = false;
    mbMesh.castShadow = false;
    mbMesh.receiveShadow = true;
    ctx.scene.add(mbMesh);
    // One small glint, on the nearest unfound book within a few metres: a catch of light, not a marker.
    const g = new THREE.RingGeometry(0.16, 0.3, 16);
    g.rotateX(-Math.PI / 2);
    mbGlint = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0xfff2c0, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
    mbGlint.name = 'matchbook-glint';
    mbGlint.visible = false;
    ctx.scene.add(mbGlint);
  }

  const _m = new THREE.Matrix4();
  const _q = new THREE.Quaternion();
  const _p = new THREE.Vector3();
  const _s = new THREE.Vector3(1, 1, 1);
  const _c = new THREE.Color();
  function syncMatchbookView(): void {
    if (!mbMesh) return;
    const a = rec();
    const show = offline();
    // Going online (or back) shows / hides them all.
    if (show !== mbShown) {
      mbShown = show;
      mbDirty = true;
    }
    if (mbDirty) {
      mbDirty = false;
      let n = 0;
      for (const s of spots) {
        if (!show || a.matchbooks.includes(s.spot.id)) continue;
        const k = MATCHBOOK_SPOTS.indexOf(s.spot);
        _q.setFromAxisAngle(_p.set(0, 1, 0), k * 1.7);
        _m.compose(_p.set(s.pos.x, (s.alley ? 0 : PAVE_Y) + 0.016, s.pos.z), _q, _s);
        mbMesh.setMatrixAt(n, _m);
        mbMesh.setColorAt(n, _c.setHex(s.spot.colour));
        n++;
      }
      mbMesh.count = n;
      mbMesh.instanceMatrix.needsUpdate = true;
      if (mbMesh.instanceColor) mbMesh.instanceColor.needsUpdate = true;
    }
    if (!mbGlint) return;
    const p = ctx.services.get('player').pos;
    let best: { pos: V2; alley: boolean } | null = null;
    let bd = MATCHBOOK_GLINT;
    if (show) {
      for (const s of spots) {
        if (a.matchbooks.includes(s.spot.id)) continue;
        const d = distance(p, s.pos);
        if (d < bd) {
          bd = d;
          best = s;
        }
      }
    }
    mbGlint.visible = !!best;
    if (best) {
      const pulse = Math.max(0, Math.sin(time * 2.4)) ** 6;
      mbGlint.position.set(best.pos.x, (best.alley ? 0 : PAVE_Y) + 0.04, best.pos.z);
      mbGlint.scale.setScalar(1 + pulse * 1.1);
      (mbGlint.material as THREE.MeshBasicMaterial).opacity = 0.2 + pulse * 0.75;
    }
  }

  // ------------------------------------------------------------------ the motor and the workshop

  function driven(): Vehicle | null {
    const player = ctx.services.get('player');
    return player.mode === 'driving' ? player.vehicle : null;
  }

  /** The build sheet for this car: the motor's own if it is the motor, else what it looks like now. */
  function buildOf(v: Vehicle): CarBuild {
    const m = rec().motor;
    if (m && v.id === motorId) return { ...m.build };
    const look = v.data[CAR_LOOK_KEY] as CarLook | undefined;
    const t = carTraits(v.id, v.spec);
    return {
      ...emptyBuild(),
      paint: v.paint,
      plate: look?.plate ?? t.plate,
      metallic: look?.metallic ?? t.metallic,
      wheels: look?.wheels ?? null,
      matt: look?.matt === true,
      rim: look?.rim ?? null,
      neon: look?.neon ?? null,
      ride: look?.ride ?? 0,
      camber: look?.camber ?? 0,
      track: look?.track ?? 0,
    };
  }

  function applyBuild(v: Vehicle, b: CarBuild): void {
    if (b.paint !== null) v.paint = b.paint;
    const look: CarLook = { metallic: b.metallic };
    if (b.wheels) look.wheels = b.wheels;
    if (b.matt) look.matt = true;
    if (b.rim !== null) look.rim = b.rim;
    if (b.neon !== null) look.neon = b.neon;
    if (b.ride !== 0) look.ride = b.ride;
    if (b.camber !== 0) look.camber = b.camber;
    if (b.track !== 0) look.track = b.track;
    if (b.trim !== null) look.trim = b.trim;
    if (b.plate) look.plate = b.plate;
    v.data[CAR_LOOK_KEY] = look;
    v.data[CAR_LOOK_VERSION_KEY] = ((v.data[CAR_LOOK_VERSION_KEY] as number | undefined) ?? 0) + 1;
    const tune = tuneFor(b);
    if (tune) v.data.tune = tune;
    else delete v.data.tune;
  }

  /** Remember `v` as the player's motor with build `b`. */
  function keepMotor(v: Vehicle, b: CarBuild): void {
    if (motorId >= 0 && motorId !== v.id) ctx.services.get('vehicles').get(motorId)?.tags.delete(MOTOR_TAG);
    motorId = v.id;
    v.tags.add(MOTOR_TAG);
    v.ownedByPlayer = true;
    rec().motor = { spec: v.spec.id, build: { ...b } };
    save().markDirty();
  }

  /** Session start: the motor waits on Halloran's forecourt (unless it's a Vesper and the lockup car carries it). */
  function spawnMotorAtYard(): void {
    const m = rec().motor;
    if (!m || !garagePoi || !offline() || inLockup()) return;
    if (motorId >= 0 && ctx.services.get('vehicles').get(motorId)) return;
    const vehicles = ctx.services.get('vehicles');
    const layout = ctx.services.get('layout');
    let best: { pos: V2; heading: number } | null = null;
    let bd = 40;
    for (const s of layout.parkedSpots) {
      const d = distance(s.pos, garagePoi.pos);
      if (d < bd) {
        bd = d;
        best = s;
      }
    }
    const f = forward(garagePoi.heading);
    const spot = best ?? { pos: { x: garagePoi.pos.x + f.x * 5, z: garagePoi.pos.z + f.z * 5 }, heading: garagePoi.heading + Math.PI / 2 };
    for (const q of vehicles.query(spot.pos, 4.5)) if (!q.isPolice && !q.tags.has('mission') && q !== ctx.services.get('player').vehicle) vehicles.despawn(q);
    let v: Vehicle;
    try {
      v = vehicles.spawn(m.spec, spot.pos, { heading: spot.heading, ownedByPlayer: true, parked: true, paint: m.build.paint ?? undefined, tags: [MOTOR_TAG] });
    } catch {
      return; // a spec that no longer exists: Des has scrapped it
    }
    motorId = v.id;
    applyBuild(v, m.build);
  }

  const garageService: GarageService = {
    dress(v) {
      const m = rec().motor;
      if (!m || !offline() || m.spec !== v.spec.id) return;
      // The motor can only be in one place: the lockup takes over from the forecourt.
      if (motorId >= 0 && motorId !== v.id) {
        const old = ctx.services.get('vehicles').get(motorId);
        if (old && ctx.services.get('player').vehicle !== old) ctx.services.get('vehicles').despawn(old);
      }
      motorId = v.id;
      v.tags.add(MOTOR_TAG);
      applyBuild(v, m.build);
    },
    buildOf,
    motorSpec: () => rec().motor?.spec ?? null,
    isMine: (v) => v.id === motorId || v.tags.has('lockup:car'),
    preview: applyBuild,
    keep(v, b) {
      keepMotor(v, b);
      applyBuild(v, b);
    },
  };

  /** The job card: the car on the forecourt, its build as paid for, and what's being tried on. */
  let cardCar: Vehicle | null = null;
  let committed: CarBuild = emptyBuild();
  /** Option index per row (paint, wheels, trim 0 = none, plate) and the metallic toggle. */
  const pending = { paint: 0, metallic: false, wheels: 0, trim: 0, plate: 0 };
  /** Rows the player has changed (← →); untouched rows show the car as it is and cost nothing. */
  const touched = new Set<string>();
  let note: Record<string, string> = {};

  /** At Des's forecourt in a car, with nothing else going on (the workshop would open, or says why not). */
  function atWorkshop(): boolean {
    return (
      !!garagePoi &&
      !!driven() &&
      offline() &&
      streetUnlocked() &&
      ctx.state.mode === 'playing' &&
      ctx.state.activeMissionId === null &&
      !economy()?.outcomePending
    );
  }

  /** Why Des won't open up right now (null = he will). Shown on the prompt instead of the prompt vanishing. */
  function workshopRefusal(): string | null {
    const v = driven();
    if (!v) return null;
    if (ctx.state.heat > 0) return 'not while the police are after you';
    if (!canWorkOn(v.spec)) return v.spec.class === 'police' ? 'he won’t touch a police car' : 'he won’t touch a lorry';
    if (v.speed >= 4) return 'stop the car on the forecourt first';
    return null;
  }

  function workshopOpen(): boolean {
    return atWorkshop() && workshopRefusal() === null;
  }

  function resetPending(): void {
    const b = committed;
    pending.paint = Math.max(0, PAINTS.findIndex((p) => p.hex === b.paint));
    pending.metallic = b.metallic;
    pending.wheels = Math.max(0, WHEELS.findIndex((w) => w.id === b.wheels));
    pending.trim = b.trim === null ? 0 : Math.max(0, TRIM_COLOURS.findIndex((c) => c.hex === b.trim)) + 1;
    pending.plate = Math.max(0, PLATES.indexOf(b.plate ?? ''));
    touched.clear();
    note = {};
  }

  /** The build as tried on: what's paid for, with every touched row's choice laid over it. */
  function previewBuild(): CarBuild {
    const b = committed;
    return {
      ...b,
      paint: touched.has('paint') ? PAINTS[pending.paint]!.hex : b.paint,
      metallic: touched.has('metallic') ? pending.metallic : b.metallic,
      wheels: touched.has('wheels') ? WHEELS[pending.wheels]!.id : b.wheels,
      trim: touched.has('trim') ? (pending.trim === 0 ? null : TRIM_COLOURS[pending.trim - 1]!.hex) : b.trim,
      plate: touched.has('plate') ? PLATES[pending.plate]! : b.plate,
    };
  }

  const choose = (): string => (touchUi.enabled ? '◂ ▸ to choose' : '← → to choose');

  function rows(): GarageRow[] {
    const v = cardCar;
    if (!v) return [{ id: 'done', label: 'Done', price: null }];
    const b = committed;
    const w = previewBuild();
    const fav = favour();
    const terms = contactPerk(ctx, 'halloran-work');
    const out: GarageRow[] = [];
    const changed = (id: string, same: boolean): boolean => touched.has(id) && !same;
    out.push({
      id: 'paint',
      label: 'New paint',
      value: paintName(w.paint ?? v.paint),
      cycles: true,
      price: changed('paint', w.paint === b.paint) ? price(PRICES.respray, fav, terms) : null,
      note: note.paint ?? (touched.has('paint') ? `Now: ${paintName(b.paint ?? v.paint)}` : choose()),
    });
    out.push({
      id: 'metallic',
      label: 'Finish',
      value: w.metallic ? 'Metallic flake' : 'Solid gloss',
      cycles: true,
      price: changed('metallic', w.metallic === b.metallic) ? price(PRICES.metallic, fav, terms) : null,
      note: note.metallic ?? (touched.has('metallic') ? `Now: ${b.metallic ? 'metallic' : 'solid'}` : choose()),
    });
    const wheel = WHEELS.find((x) => x.id === w.wheels);
    out.push({
      id: 'wheels',
      label: 'Wheels',
      value: wheel?.name ?? 'As it came',
      cycles: true,
      price: changed('wheels', w.wheels === b.wheels) && wheel ? price(wheel.price, fav, terms) : null,
      note: note.wheels ?? (touched.has('wheels') ? `Now: ${WHEELS.find((x) => x.id === b.wheels)?.name ?? 'as it came'}` : choose()),
    });
    const tn = trimName(v.spec);
    if (tn) {
      out.push({
        id: 'trim',
        label: tn,
        value: TRIM_COLOURS.find((c) => c.hex === w.trim)?.name ?? 'None',
        cycles: true,
        price: changed('trim', w.trim === b.trim) ? price(PRICES.trim, fav, terms) : null,
        note: note.trim ?? (touched.has('trim') ? `Now: ${TRIM_COLOURS.find((c) => c.hex === b.trim)?.name ?? 'none'}` : choose()),
      });
    }
    out.push({
      id: 'plate',
      label: 'Plates',
      value: w.plate ?? '',
      cycles: true,
      price: changed('plate', w.plate === b.plate) ? price(PRICES.plate, fav, terms) : null,
      note: note.plate ?? (touched.has('plate') ? `Now: ${b.plate ?? '—'}` : choose()),
    });
    const nextTune = TUNES.find((t) => t.stage === b.tune + 1);
    out.push({
      id: 'tune',
      label: 'Engine',
      value: nextTune ? nextTune.name : `Stage ${b.tune}, fully tuned`,
      price: nextTune ? price(nextTune.price, fav, terms) : null,
      note: note.tune ?? (nextTune ? `${nextTune.detail}. Now: ${b.tune ? `stage ${b.tune}` : 'as it left the factory'}` : 'Nothing more Des can do'),
    });
    const fix = repairCost(v.health, v.spec.maxHealth);
    out.push({
      id: 'repair',
      label: 'Body work',
      value: fix > 0 ? `${Math.round((1 - v.health / v.spec.maxHealth) * 100)} % damaged` : 'Not a scratch',
      price: fix > 0 ? price(fix, fav, terms) : null,
      note: note.repair ?? '',
    });
    out.push({ id: 'done', label: 'Done', value: 'Drive it away', price: null });
    return out;
  }

  function cycle(id: string, dir: 1 | -1): void {
    const v = cardCar;
    if (!v) return;
    const wrap = (n: number, len: number): number => ((n % len) + len) % len;
    // From the car as it is, the first press goes to the neighbour of its current option (or the first one).
    const from = (cur: boolean): number => (touched.has(id) || cur ? dir : 0);
    if (id === 'paint') pending.paint = wrap(pending.paint + from(PAINTS[pending.paint]!.hex === committed.paint), PAINTS.length);
    else if (id === 'metallic') pending.metallic = !pending.metallic;
    else if (id === 'wheels') pending.wheels = wrap(pending.wheels + from(WHEELS[pending.wheels]!.id === committed.wheels), WHEELS.length);
    else if (id === 'trim') pending.trim = wrap(pending.trim + dir, TRIM_COLOURS.length + 1);
    else if (id === 'plate') pending.plate = wrap(pending.plate + from(PLATES[pending.plate] === committed.plate), PLATES.length);
    else return;
    touched.add(id);
    delete note[id];
    applyBuild(v, previewBuild());
  }

  function buy(id: string): boolean {
    const v = cardCar;
    if (!v) return false;
    const row = rows().find((r) => r.id === id);
    if (!row || row.price === null) return false;
    const cost = row.price;
    const eco = economy();
    if (!eco || !eco.spend(cost, `garage-${id}`)) {
      note[id] = 'Not enough cash.';
      return false;
    }
    const want = previewBuild();
    const b: CarBuild = { ...committed };
    if (id === 'paint') {
      b.paint = want.paint;
      v.stolen = false; // a new colour, and Des does the log book
    } else if (id === 'metallic') b.metallic = want.metallic;
    else if (id === 'wheels') b.wheels = want.wheels;
    else if (id === 'trim') b.trim = want.trim;
    else if (id === 'plate') b.plate = want.plate;
    else if (id === 'tune') b.tune = Math.min(TUNES.length, b.tune + 1);
    else if (id === 'repair') ctx.services.get('vehicles').repair(v);
    if (b.paint === null) b.paint = v.paint;
    committed = b;
    touched.delete(id);
    // Body work alone doesn't make a car yours: Des only opens a build sheet for something he's dressed or tuned.
    if (id !== 'repair' || v.id === motorId) keepMotor(v, b);
    rec().garageSpent += cost;
    note[id] = 'Done.';
    applyBuild(v, previewBuild());
    ctx.events.emit('garageWork', { vehicleId: v.id, job: id, cost });
    ui()?.toast(`${GARAGE_OWNER}: “${GARAGE_LINES[id] ?? 'There you go.'}” −£${formatMoney(cost)}`, 'good', 2.6);
    return true;
  }

  const GARAGE_LINES: Record<string, string> = {
    paint: 'Lovely colour. Nobody’ll recognise it.',
    metallic: 'Catches the light, that.',
    wheels: 'Proper wheels now.',
    trim: 'Very tasty.',
    plate: 'Don’t ask where they came from.',
    tune: 'Mind how you go with that.',
    repair: 'Good as new, near enough.',
  };

  /** The card's sub-line: Des's motto — or, with another car on the card, a warning that it takes the one build sheet over. */
  function subFor(v: Vehicle | null): string {
    const m = rec().motor;
    return m && v && v.id !== motorId
      ? `${GARAGE_OWNER} · "One build sheet, mind. Dress this one and I forget your ${modelName(m.spec)}."`
      : `${GARAGE_OWNER} · "If it’s got wheels, I can make it nicer."`;
  }

  const model: GarageModel = {
    title: 'Halloran Motors — workshop',
    get sub() {
      return subFor(cardCar);
    },
    balance: () => economy()?.balance ?? ctx.state.money,
    rows,
    cycle,
    buy,
    close: () => closeCard(),
    touch: () => touchUi.enabled,
    sound: (s) => ctx.services.tryGet('audio')?.ui(s),
  };

  /** What the car looked like when it rolled in (put back if nothing was paid for). */
  let before: { paint: number; look: unknown; tune: unknown } | null = null;

  /** Put a car on the job card (with or without a screen: the tests use the card headless). */
  function beginCard(v: Vehicle): void {
    cardCar = v;
    committed = buildOf(v);
    before = { paint: v.paint, look: v.data[CAR_LOOK_KEY], tune: v.data.tune };
    resetPending();
  }

  /** Off the card: anything tried on but not paid for comes off again. */
  function endCard(): void {
    const v = cardCar;
    cardCar = null;
    if (v && ctx.services.get('vehicles').get(v.id) && touched.size) {
      if (v.id === motorId) applyBuild(v, committed);
      else if (before) {
        v.paint = before.paint;
        if (before.look === undefined) delete v.data[CAR_LOOK_KEY];
        else v.data[CAR_LOOK_KEY] = before.look;
        if (before.tune === undefined) delete v.data.tune;
        else v.data.tune = before.tune;
        v.data[CAR_LOOK_VERSION_KEY] = ((v.data[CAR_LOOK_VERSION_KEY] as number | undefined) ?? 0) + 1;
      }
    }
    touched.clear();
    before = null;
  }

  function openCard(): boolean {
    const u = ui();
    const v = driven();
    if (!u || !ctx.uiRoot || !v || screen) return false;
    const root = ctx.uiRoot.querySelector('.bh-ui') as HTMLElement | null;
    if (!root) return false;
    beginCard(v);
    const s = new GarageScreen(root, model);
    if (!u.openOverlay(s, 'garage', () => teardown(s))) {
      s.dispose();
      endCard();
      return false;
    }
    screen = s;
    ctx.uiRoot.classList.add('gw-open');
    ctx.services.tryGet('audio')?.ui('whoosh');
    // Like the book: the borough waits while you choose a colour.
    if (ctx.loop.timeScale > 0) {
      frozeTime = true;
      ctx.loop.timeScale = 0;
    }
    ctx.services.get('player').controls.acquire('garageCard');
    ctx.input.clearTouch?.();
    return true;
  }

  function teardown(s: GarageScreen): void {
    if (screen !== s) return;
    screen = null;
    s.dispose();
    ctx.uiRoot?.classList.remove('gw-open');
    endCard();
    if (frozeTime) {
      frozeTime = false;
      if (ctx.loop.timeScale === 0) ctx.loop.timeScale = 1;
    }
    ctx.services.get('player').controls.release('garageCard');
  }

  function closeCard(): void {
    const s = screen;
    if (!s) return;
    ui()?.closeOverlay(s);
    teardown(s);
  }

  // ------------------------------------------------------------------ system

  return {
    id: 'activities',
    order: 535,
    init(c) {
      ctx = c;
      c.services.provide('garage', garageService);
      c.services.provide('activities', {
        get race() {
          return shared.race;
        },
        get matchbooks() {
          return { found: rec().matchbooks.length, total: spots.length };
        },
      });
      garagePoi = c.services.get('layout').pois.find((p) => p.id === GARAGE_POI) ?? null;
      buildDefs();
      buildSpots();
      c.services.get('missions').registerActivities(offered);

      offs.push(
        c.events.on('missionCompleted', (e) => {
          if (e.id === RACE_ID || e.id === CAB_ID || e.replay) return;
          // The first story job opens the street jobs — and with them the side lines.
          queueMicrotask(announce);
        }),
        c.events.on('newGameStarted', () => {
          raceRuntime.close();
          host.ticket(null);
          motorId = -1;
          mbDirty = true;
        }),
        c.events.on('vehicleDestroyed', (e) => {
          if (e.vehicleId === motorId) motorId = -1;
        }),
      );

      c.hooks.register('activityState', () => {
        const a = rec();
        const s = shared.race;
        return {
          race: { ...a.race, best: [...a.race.best], cleared: [...a.race.cleared] },
          cab: { ...a.cab, best: [...a.cab.best], cleared: [...a.cab.cleared] },
          matchbooks: { found: [...a.matchbooks], total: spots.length, milestones: [...a.matchbookMilestones] },
          motor: a.motor ? { spec: a.motor.spec, build: { ...a.motor.build }, vehicleId: motorId } : null,
          garageSpent: a.garageSpent,
          ticket: ticketState,
          posted: [...posted],
          offered: offered().map((d) => ({ id: d.id, title: d.title, tag: d.tag })),
          session: s
            ? {
                tier: s.tier.tier,
                started: s.started,
                clock: +s.clock.toFixed(2),
                passed: s.playerPassed,
                total: s.total,
                place: s.place(),
                finishedAt: s.playerFinishedAt,
                rivals: s.rivals.map((r) => ({ name: r.name, id: r.vehicle.id, passed: r.passed, finishedAt: r.finishedAt, out: r.out, hops: r.driver.hops, x: +r.vehicle.pos.x.toFixed(1), z: +r.vehicle.pos.z.toFixed(1) })),
                checkpoints: s.line.checkpoints.map((cp) => ({ x: +cp.pos.x.toFixed(1), z: +cp.pos.z.toFixed(1), r: cp.radius })),
                line: s.line.points.map((p) => ({ x: +p.x.toFixed(2), z: +p.z.toFixed(2), ...(p.cap !== undefined ? { cap: p.cap } : {}) })),
              }
            : null,
          bests: { race: a.race.best.map((b) => (b > 0 ? formatRaceTime(b) : null)), cab: [...a.cab.best] },
        };
      });
      c.hooks.register('activityTier', (kind, tier) => {
        const r = kind === 'race' ? rec().race : rec().cab;
        const max = kind === 'race' ? RACE_TIERS : CAB_TIERS;
        r.unlocked = Math.max(r.unlocked, Math.min(max, Math.max(1, Math.round(tier))));
        r.next = Math.min(r.unlocked, Math.max(1, Math.round(tier)));
        save().markDirty();
        return r.next;
      });
      c.hooks.register('matchbookSpots', () => spots.map((s) => ({ id: s.spot.id, venue: s.spot.venue, x: s.pos.x, z: s.pos.z, found: rec().matchbooks.includes(s.spot.id) })));
      c.hooks.register('activityPoint', (what) => {
        if (what === 'race-grid') {
          const l = lines[rec().race.next - 1] ?? lines[0];
          return l ? { x: l.start.x, z: l.start.z } : null;
        }
        if (what === 'cab-pickup') {
          const d = cabDefs[rec().cab.next - 1] ?? cabDefs[0];
          const st = d?.stages[0];
          return st && st.kind === 'custom' && st.at && 'pos' in st.at ? { x: st.at.pos.x, z: st.at.pos.z } : null;
        }
        return garagePoi ? { x: garagePoi.pos.x, z: garagePoi.pos.z } : null;
      });
      c.hooks.register('garageState', () => ({ open: !!screen, available: workshopOpen(), rows: cardCar ? rows() : [], motorId, sub: subFor(cardCar ?? driven()) }));
      c.hooks.register('garageBuy', (job, steps = 0) => {
        // Headless / scripted: the same card, without the screen. Only where the card would open.
        const v = driven();
        if (!v || (!screen && !workshopOpen())) return { ok: false, reason: 'not at the workshop' };
        const own = !screen;
        if (own) beginCard(v);
        for (let i = 0; i < steps; i++) cycle(job, 1);
        const ok = buy(job);
        const reason = ok ? null : note[job] ?? 'nothing to buy';
        if (own) endCard();
        else screen?.render();
        return { ok, reason };
      });
      c.hooks.registerSnapshot('activities', () => ({
        race: shared.race ? { place: shared.race.place(), passed: shared.race.playerPassed, total: shared.race.total } : null,
        matchbooks: rec().matchbooks.length,
      }));
    },
    start() {
      const u = ui();
      if (ctx.uiRoot) {
        const hud = (ctx.uiRoot.querySelector('.bh-hud') ?? ctx.uiRoot.querySelector('.bh-ui')) as HTMLElement | null;
        if (hud) ticket = new ActivityTicket(hud);
      }
      buildMatchbookView();
      offs.push(ctx.services.tryGet('book')?.addJobsSource(jobsEntries) ?? (() => undefined));
      // Race markers: the gate after next (faint) and the rivals; the workshop on the map once it's open.
      if (u) {
        offs.push(
          u.addMarkerSource((out) => {
            if (ctx.state.mode !== 'playing' && ctx.state.mode !== 'paused') return;
            const s = shared.race;
            if (s?.started) {
              const n = s.afterNext();
              if (n) out.push({ x: n.x, z: n.z, kind: 'target', glyph: String(Math.min(9, s.playerPassed + 2)), pin: false });
              for (const p of s.rivalPositions()) out.push({ x: p.x, z: p.z, kind: 'hazard', pin: false });
            }
            if (garagePoi && offline() && streetUnlocked() && ctx.state.activeMissionId === null) {
              out.push({ x: garagePoi.pos.x + 3, z: garagePoi.pos.z + 3, kind: 'friend', glyph: 'W', pin: false, label: 'Des — the workshop' });
            }
          }),
        );
      }
      if (garagePoi) {
        const p = garagePoi;
        offs.push(
          ctx.services.get('interactions').register({
            id: 'activity:workshop',
            pos: () => p.pos,
            radius: GARAGE_RADIUS,
            priority: 3,
            whileDriving: true,
            enabled: workshopOpen,
            prompt: () => `Des’s workshop — paint, wheels, plates, a tune${favour() ? ' (15 % off)' : ''}`,
            onInteract: () => {
              openCard();
            },
          }),
          // The same forecourt when Des won't work: says why (wanted, rolling, a car he won't touch) instead of
          // the prompt just vanishing. Low priority so the patch-up and respray shops still win when they apply.
          ctx.services.get('interactions').register({
            id: 'activity:workshop-closed',
            pos: () => p.pos,
            radius: GARAGE_RADIUS,
            priority: 6,
            whileDriving: true,
            enabled: () => atWorkshop() && workshopRefusal() !== null,
            prompt: () => `Des’s workshop — ${workshopRefusal() ?? 'closed'}`,
            onInteract: () => {
              const why = workshopRefusal();
              if (why) ui()?.toast(`Des’s workshop: ${why}.`, 'warn', 2.6);
            },
          }),
        );
      }
      spawnMotorAtYard();
      announce();
    },
    fixedUpdate() {
      checkMatchbooks();
      // A race whose job has gone (abort, new game, online): no rivals left driving about.
      if (shared.race && ctx.state.activeMissionId !== RACE_ID) raceRuntime.close();
      if (ticketState && ctx.state.activeMissionId === null) host.ticket(null);
    },
    update(dt) {
      time += dt;
      syncMatchbookView();
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      if (screen) closeCard();
      raceRuntime.close();
      ticket?.dispose();
      if (mbMesh) {
        ctx.scene.remove(mbMesh);
        mbMesh.geometry.dispose();
        (mbMesh.material as THREE.Material).dispose();
        mbMesh = null;
      }
      if (mbGlint) {
        ctx.scene.remove(mbGlint);
        mbGlint.geometry.dispose();
        (mbGlint.material as THREE.Material).dispose();
        mbGlint = null;
      }
    },
  };
}
